<template>
  <IntercomLayout>
    <template #topbar>
      <SevaTopbar
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        @settings="$router.push('/settings')"
        @logout="handleLogout"
      />
    </template>

    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <div class="expense-detail-view">
      <!-- Back Button -->
      <IntercomButton variant="ghost" size="sm" @click="$router.push('/expenses')" class="back-button">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
        {{ $t('common.back') }}
      </IntercomButton>

      <div v-if="isLoading" class="loading-state">
        {{ $t('common.loading') }}
      </div>

      <template v-else-if="expense">
        <!-- Page Header -->
        <div class="page-header">
          <div class="header-info">
            <h1 class="page-title">{{ expense.expense_number }}</h1>
            <IntercomBadge v-if="expense.category" variant="info">
              {{ expense.category.name }}
            </IntercomBadge>
          </div>
          <div class="header-actions">
            <IntercomButton variant="secondary" @click="showEditModal = true">
              {{ $t('common.edit') }}
            </IntercomButton>
            <IntercomButton variant="ghost" class="delete-btn" @click="handleDelete">
              {{ $t('common.delete') }}
            </IntercomButton>
          </div>
        </div>

        <!-- Stats Row -->
        <div class="stats-row">
          <IntercomCard class="stat-card primary">
            <div class="stat-label">{{ $t('expense.amount') }}</div>
            <div class="stat-value">{{ formatCompactCurrency(expense.amount || 0) }}</div>
          </IntercomCard>
          <IntercomCard class="stat-card">
            <div class="stat-label">{{ $t('expense.date') }}</div>
            <div class="stat-value">{{ formatDate(expense.expense_date) }}</div>
          </IntercomCard>
          <IntercomCard v-if="expense.vendor_name" class="stat-card">
            <div class="stat-label">{{ $t('expense.vendor') }}</div>
            <div class="stat-value vendor-value">{{ expense.vendor_name }}</div>
          </IntercomCard>
          <IntercomCard v-if="expense.bank_account_name" class="stat-card">
            <div class="stat-label">{{ $t('expense.paidFrom') || 'Paid From' }}</div>
            <div class="stat-value vendor-value">{{ expense.bank_account_name }}</div>
          </IntercomCard>
        </div>

        <!-- Details Card -->
        <IntercomCard v-if="expense.description || expense.notes || expense.reference_number" class="details-card">
          <h3 class="details-title">{{ $t('common.details') }}</h3>
          <div v-if="expense.description" class="detail-row">
            <span class="detail-label">{{ $t('expense.description') }}</span>
            <span class="detail-value">{{ expense.description }}</span>
          </div>
          <div v-if="expense.reference_number" class="detail-row">
            <span class="detail-label">{{ $t('expense.invoiceNumber') }}</span>
            <span class="detail-value">{{ expense.reference_number }}</span>
          </div>
          <div v-if="expense.payment_mode" class="detail-row">
            <span class="detail-label">{{ $t('donation.type') }}</span>
            <span class="detail-value">{{ expense.payment_mode }}</span>
          </div>
          <div v-if="expense.notes" class="detail-row">
            <span class="detail-label">{{ $t('expense.notes') }}</span>
            <span class="detail-value">{{ expense.notes }}</span>
          </div>
        </IntercomCard>

        <!-- Danger Zone -->
        <div class="danger-zone">
          <IntercomButton variant="ghost" class="delete-button" @click="handleDelete">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
            {{ $t('common.delete') }} {{ $t('expense.title').toLowerCase() }}
          </IntercomButton>
        </div>
      </template>

      <!-- Edit Modal -->
      <IntercomModal v-model:show="showEditModal" :title="$t('expense.editExpense')">
        <ExpenseForm
          :expense="expense"
          :categories="categories"
          :programs="programs"
          :loading="isSaving"
          @submit="handleSave"
          @cancel="showEditModal = false"
        />
      </IntercomModal>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useDialog } from 'naive-ui'
import { useToast } from '@shared/composables/useToast.js'
import { useAuth } from '@shared/composables/useAuth.js'
import { useBusiness } from '@shared/composables/useBusiness.js'
import { useSeva } from '../composables/useSeva.js'
import { formatCompactCurrency } from '@shared/utils/currency.js'
import {
  IntercomLayout,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomBadge,
  IntercomModal,
} from '@shared/components/intercom'
import SevaTopbar from '../components/SevaTopbar.vue'
import ExpenseForm from '../components/ExpenseForm.vue'
import { useNavigation } from '../composables/useNavigation.js'
import dayjs from 'dayjs'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const toast = useToast()
const dialog = useDialog()
const { user, globalUser, logout } = useAuth()
const { businessName, fetchBusiness } = useBusiness()
const { getExpense, updateExpense, deleteExpense, listExpenseCategories, listPrograms } = useSeva()
const { navSections } = useNavigation()

// State
const expense = ref(null)
const categories = ref([])
const programs = ref([])
const isLoading = ref(false)
const isSaving = ref(false)
const showEditModal = ref(false)

const formatDate = (date) => date ? dayjs(date).format('DD MMM YYYY') : ''

const fetchExpenseData = async () => {
  const expenseId = route.params.id
  isLoading.value = true
  try {
    const response = await getExpense(expenseId)
    // apiClient.get() unwraps to { expense: {...} }, extract the inner object
    expense.value = response?.expense || response
  } catch (error) {
    console.error('Failed to fetch expense:', error)
    toast.error(t('errors.loadFailed'))
  } finally {
    isLoading.value = false
  }
}

const fetchCategories = async () => {
  try {
    const response = await listExpenseCategories()
    // apiClient.get() unwraps to { categories: [...] }
    categories.value = response?.categories || []
  } catch (error) {
    console.error('Failed to fetch categories:', error)
  }
}

const fetchPrograms = async () => {
  try {
    const response = await listPrograms()
    programs.value = response?.programs || []
  } catch (error) {
    console.error('Failed to fetch programs:', error)
  }
}

const handleSave = async (formData) => {
  isSaving.value = true
  try {
    await updateExpense(expense.value.id, formData)
    toast.success(t('success.expenseUpdated'))
    showEditModal.value = false
    fetchExpenseData()
  } catch (error) {
    console.error('Failed to save expense:', error)
    toast.error(t('errors.saveFailed'))
  } finally {
    isSaving.value = false
  }
}

const handleDelete = () => {
  dialog.warning({
    title: t('common.confirm'),
    content: t('expense.deleteConfirm'),
    positiveText: t('common.delete'),
    negativeText: t('common.cancel'),
    onPositiveClick: async () => {
      try {
        await deleteExpense(expense.value.id)
        toast.success(t('success.expenseDeleted'))
        router.push('/expenses')
      } catch (error) {
        console.error('Failed to delete expense:', error)
        toast.error(t('errors.deleteFailed'))
      }
    },
  })
}

const handleLogout = async () => {
  await logout()
  router.push('/login')
}

onMounted(() => {
  fetchBusiness()
  fetchCategories()
  fetchExpenseData()
  fetchPrograms()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.expense-detail-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.back-button {
  margin-bottom: var(--space-4);
}

.loading-state {
  text-align: center;
  padding: var(--space-12);
  color: var(--color-gray-500);
}

/* Page Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--space-6);
  flex-wrap: wrap;
  gap: var(--space-4);
}

.header-info {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0;
}

.header-actions {
  display: flex;
  gap: var(--space-3);
}

.delete-btn {
  color: var(--color-gray-500);
}

/* Stats Row */
.stats-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: var(--space-4);
  margin-bottom: var(--space-6);
}

.stat-card {
  padding: var(--space-4);
  text-align: center;
}

.stat-card.primary {
  background: var(--color-blue-50, #eff6ff);
  border-color: var(--color-blue-200, #bfdbfe);
}

.stat-card.primary .stat-value {
  color: var(--color-blue-700, #1d4ed8);
}

.stat-label {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  margin-bottom: var(--space-1);
}

.stat-value {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
}

.vendor-value {
  font-size: var(--font-size-base);
  word-break: break-word;
}

/* Details Card */
.details-card {
  margin-bottom: var(--space-6);
}

.details-title {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-4) 0;
  padding-bottom: var(--space-3);
  border-bottom: 1px solid var(--color-gray-200);
}

.detail-row {
  display: flex;
  gap: var(--space-4);
  padding: var(--space-3) 0;
  border-bottom: 1px solid var(--color-gray-100);
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  width: 150px;
  color: var(--color-gray-500);
  font-size: var(--font-size-sm);
  flex-shrink: 0;
}

.detail-value {
  flex: 1;
  color: var(--color-charcoal);
}

/* Danger Zone */
.danger-zone {
  margin-top: var(--space-8);
  padding-top: var(--space-6);
  border-top: 1px solid var(--color-gray-200);
}

.delete-button {
  color: var(--color-red-600, #dc2626) !important;
}

.delete-button:hover {
  background: var(--color-red-50, #fef2f2) !important;
}

@media (max-width: 768px) {
  .expense-detail-view {
    padding: var(--space-4);
  }

  .page-header {
    flex-direction: column;
  }

  .stats-row {
    grid-template-columns: 1fr;
  }
}
</style>
